<template>
  <div>
    <v-container fluid style="max-width: none !important; width: 100%;">
      <!-- Header -->
      <v-skeleton-loader :loading="!flagNav" transition="scale-transition" max-height="64" type="table-heading">
        <v-toolbar dark class="header-title text-white">
          <v-toolbar-title class="title" :style="'color: #' + FontColorTitle">Target Phrase Management</v-toolbar-title>
          <v-spacer />
          <div class="d-flex align-center gap-3 pr-4">
            <v-toolbar-title class="title" :style="'color: #' + FontColorTitle">{{ today }}</v-toolbar-title>
          </div>
        </v-toolbar>
      </v-skeleton-loader>

      <v-card class="card2" style="margin-top: 1rem">
        <v-container style="max-width: none !important; width: 100%;" class="px-6 py-6">
          <!-- Info Card -->
          <v-alert type="warning" variant="tonal" class="mb-4">
            <strong>Target Phrases</strong> are used for brand name recognition in speech-to-text.
            Add phrases like "B You", "B You Max", "B You Wealth", etc. to improve recognition accuracy.
          </v-alert>

          <!-- Search and Add -->
          <v-row class="mb-4">
            <v-col cols="12" md="6">
              <v-text-field
                v-model="search"
                placeholder="Search phrases..."
                prepend-inner-icon="mdi-magnify"
                variant="outlined"
                rounded
                clearable
                hide-details
              />
            </v-col>
            <v-col v-if="permissions.add" cols="12" md="6" class="d-flex justify-end align-center">
              <v-btn class="btn-layout" @click="addItem">
                <v-icon start size="20">mdi-plus-circle</v-icon>
                Add Phrase
              </v-btn>
            </v-col>
          </v-row>

          <!-- Data Table -->
          <v-data-table
            :headers="headers"
            :items="filteredItems"
            :loading="loading"
            class="elevation-1 my-data-table modern-table"
            density="comfortable"
            :items-per-page="-1"
            fixed-header
            height="500"
          >
            <template v-slot:item.activeFlag="{ item }">
              <v-chip :color="item.activeFlag ? 'success' : 'error'" size="small" variant="outlined">
                {{ item.activeFlag ? 'Active' : 'Inactive' }}
              </v-chip>
            </template>

            <template v-slot:item.actions="{ item }">
              <div>
                <v-btn style="margin-right: 10px; height: 36px; width: 36px; border-radius: 6px;" size="small" variant="outlined" color="primary" @click="editItem(item)">
                  <v-icon v-if="!permissions.edit" size="16" color="primary">mdi-eye-outline</v-icon>
                  <v-img v-else :src="images.EditImg" width="14" height="14" />
                  <v-tooltip activator="parent" location="top">{{ permissions.edit ? 'Edit' : 'View' }}</v-tooltip>
                </v-btn>
                <v-btn v-if="permissions.delete" style="height: 36px; width: 36px; border-radius: 6px;" size="small" variant="outlined" color="error" @click="deleteItem(item)">
                  <v-img :src="images.DeleteImg" width="14" height="14" />
                  <v-tooltip activator="parent" location="top">Delete</v-tooltip>
                </v-btn>
              </div>
            </template>

            <template v-slot:no-data>
              <div class="text-center py-8">
                <v-icon size="64" color="grey">mdi-format-list-text</v-icon>
                <p class="text-h6 mt-4">No phrases yet</p>
                <p class="text-body-2 text-grey">Click "Add Phrase" to create your first target phrase entry</p>
              </div>
            </template>
          </v-data-table>
        </v-container>
      </v-card>
    </v-container>

    <!-- Create/Edit Dialog -->
    <v-dialog v-model="dialog" max-width="600px">
      <v-card style="border-radius: 18px !important;">
        <v-card-title class="dialog-header dialog-header-add">
          <div class="d-flex align-center">
            <div class="header-icon-container">
              <v-avatar class="icon-background" size="46">
                <v-icon color="#4b2667" size="24">
                  {{ editMode ? (permissions.edit ? 'mdi-pencil' : 'mdi-eye-outline') : 'mdi-plus' }}
                </v-icon>
              </v-avatar>
            </div>
            <div>
              <div class="dialog-title">
                {{ editMode ? (permissions.edit ? 'Edit Target Phrase' : 'View Target Phrase') : 'Add Target Phrase' }}
              </div>
              <div class="dialog-subtitle">
                Brand name recognition phrase
              </div>
            </div>
          </div>
          <v-spacer />
          <v-btn icon="mdi-close" variant="text" @click="closeDialog" />
        </v-card-title>

        <v-card-text class="pt-6">
          <v-form ref="form" v-model="valid">
            <v-row>
              <v-col cols="12">
                <v-text-field
                  v-model="formData.phrase"
                  label="Target Phrase *"
                  variant="outlined"
                  :rules="[rules.required, rules.maxLength]"
                  :maxlength="500"
                  counter
                  autofocus
                  placeholder="e.g., B You, B You Max, B You Wealth"
                />
              </v-col>
              <v-col cols="12">
                <v-switch v-model="formData.activeFlag" label="Active" color="primary" inset hide-details />
              </v-col>
            </v-row>
          </v-form>
        </v-card-text>

        <v-card-actions class="px-6 pb-6">
          <v-spacer />
          <v-btn style="margin-right: 15px;border-radius: 12px; height: 50px; width: 90px;" variant="outlined" @click="closeDialog">{{ isViewOnly ? 'Close' : 'Cancel' }}</v-btn>
          <v-btn v-if="editMode ? permissions.edit : permissions.add" class="btn-layout" :loading="saving" @click="saveItem">{{ editMode ? 'Update' : 'Add' }}</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Delete Confirmation Dialog -->
    <v-dialog v-model="deleteDialog" max-width="500px">
      <v-card style="border-radius: 18px !important;">
        <v-card-title class="dialog-header dialog-header-delete">
          <div class="d-flex align-center">
            <div class="header-icon-container">
              <v-avatar class="icon-background-delete" size="46">
                <v-icon color="#d32f2f" size="24">mdi-delete</v-icon>
              </v-avatar>
            </div>
            <div>
              <div class="dialog-title">Confirm Delete</div>
              <div class="dialog-subtitle">This action cannot be undone</div>
            </div>
          </div>
        </v-card-title>

        <v-card-text class="pt-4">
          <p>Are you sure you want to delete this target phrase?</p>
          <v-alert type="warning" variant="tonal" class="mt-4">
            <strong>Phrase:</strong> {{ itemToDelete?.phrase }}
          </v-alert>
        </v-card-text>

        <v-card-actions class="px-6 pb-6">
          <v-spacer />
          <v-btn style="margin-right: 15px;border-radius: 12px; height: 50px; width: 90px;" @click="deleteDialog = false">Cancel</v-btn>
          <v-btn v-if="permissions.delete" style="margin-right: 15px;border-radius: 12px; height: 50px; width: 90px;" color="error" :loading="saving" @click="confirmDeleteItem">Delete</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Snackbar for notifications -->
    <v-snackbar v-model="snackbar" :color="snackbarColor" :timeout="3000" location="top">
      {{ snackbarText }}
      <template v-slot:actions>
        <v-btn variant="text" @click="snackbar = false">Close</v-btn>
      </template>
    </v-snackbar>
  </div>
</template>

<script>
import Cookies from 'js-cookie';
import EditImg from '@/assets/edit1.png';
import DeleteImg from '@/assets/delete1.png';

export default {
  name: 'TargetPhraseManagement',
  data() {
    return {
      today: null,
      flagNav: true,
      loading: false,
      saving: false,
      dialog: false,
      deleteDialog: false,
      editMode: false,
      valid: false,
      search: '',

      headers: [
        { title: 'Phrase', key: 'phrase', sortable: true },
        { title: 'Status', key: 'activeFlag', sortable: true, align: 'center' },
        { title: 'Actions', key: 'actions', sortable: false, align: 'center' },
      ],

      items: [],
      itemToDelete: null,

      formData: {
        targetPhraseID: null,
        phrase: '',
        activeFlag: true,
      },

      rules: {
        required: (value) => !!value || 'This field is required',
        maxLength: (value) => !value || value.length <= 500 || 'Maximum 500 characters',
      },

      snackbar: false,
      snackbarText: '',
      snackbarColor: 'success',

      images: {
        EditImg: EditImg,
        DeleteImg: DeleteImg,
      },
    };
  },

  computed: {
    isViewOnly() {
      return this.$pagePermission && this.$pagePermission.isViewOnly('TargetPhraseManagement');
    },
    canAdd() {
      return !!(this.$pagePermission && this.$pagePermission.canAdd('TargetPhraseManagement'));
    },
    canEdit() {
      return !!(this.$pagePermission && this.$pagePermission.canEdit('TargetPhraseManagement'));
    },
    canDelete() {
      return !!(this.$pagePermission && this.$pagePermission.canDelete('TargetPhraseManagement'));
    },
    permissions() {
      return {
        add: this.$pagePermission && this.$pagePermission.canAdd('TargetPhraseManagement'),
        edit: this.$pagePermission && this.$pagePermission.canEdit('TargetPhraseManagement'),
        delete: this.$pagePermission && this.$pagePermission.canDelete('TargetPhraseManagement'),
      }
    },
    FontColorTitle() {
      return Cookies.get('fontColorTitle') || 'FFFFFF';
    },

    filteredItems() {
      if (!this.search) return this.items;
      const searchLower = this.search.toLowerCase();
      return this.items.filter(
        (item) => item.phrase?.toLowerCase().includes(searchLower)
      );
    },
  },

  mounted() {
    this.GetDate();
    this.loadTargetPhrases();
  },

  methods: {
    GetDate() {
      let today1 = new Date();
      let dd = today1.getDate();
      let mm = today1.getMonth() + 1;
      let yyyy = today1.getFullYear();
      if (dd < 10) dd = '0' + dd;
      if (mm < 10) mm = '0' + mm;
      this.today = dd + '/' + mm + '/' + yyyy;
    },

    showSnackbar(text, color = 'success') {
      this.snackbarText = text;
      this.snackbarColor = color;
      this.snackbar = true;
    },

    loadTargetPhrases() {
      this.loading = true;
      this.$call.GetTargetPhrases(
        true,
        (dataResponse) => {
          if (dataResponse.status === 0) {
            this.items = dataResponse.data || [];
          } else {
            this.items = [];
            this.showSnackbar('Failed to load target phrases', 'error');
          }
          this.loading = false;
        },
        (errorResource) => {
          this.loading = false;
          this.showSnackbar('Error loading target phrases', 'error');
          console.error(errorResource);
        }
      );
    },

    addItem() {
      this.editMode = false;
      this.formData = {
        targetPhraseID: null,
        phrase: '',
        activeFlag: true,
      };
      this.dialog = true;
    },

    editItem(item) {
      this.editMode = true;
      this.formData = { ...item };
      this.dialog = true;
    },

    deleteItem(item) {
      this.itemToDelete = item;
      this.deleteDialog = true;
    },

    async saveItem() {
      const { valid } = await this.$refs.form.validate();
      if (!valid) return;

      this.saving = true;

      const request = {
        create: [],
        update: [],
        delete: [],
      };

      if (this.editMode) {
        request.update = [{
          targetPhraseID: this.formData.targetPhraseID,
          phrase: this.formData.phrase?.trim() || '',
          activeFlag: this.formData.activeFlag,
        }];
      } else {
        request.create = [{
          phrase: this.formData.phrase?.trim() || '',
          activeFlag: this.formData.activeFlag,
        }];
      }

      this.$call.BulkSaveTargetPhraseChanges(
        request,
        (dataResponse) => {
          if (dataResponse.status === 0) {
            this.showSnackbar(this.editMode ? 'Updated successfully' : 'Added successfully', 'success');
            this.loadTargetPhrases();
            this.closeDialog();
          } else {
            this.showSnackbar(dataResponse.message || 'Failed to save', 'error');
          }
          this.saving = false;
        },
        (errorResource) => {
          this.saving = false;
          this.showSnackbar('Error saving', 'error');
          console.error(errorResource);
        }
      );
    },

    confirmDeleteItem() {
      if (!this.itemToDelete) return;

      this.saving = true;

      const request = {
        create: [],
        update: [],
        delete: [this.itemToDelete.targetPhraseID],
      };

      this.$call.BulkSaveTargetPhraseChanges(
        request,
        (dataResponse) => {
          if (dataResponse.status === 0) {
            this.showSnackbar('Deleted successfully', 'success');
            this.loadTargetPhrases();
          } else {
            this.showSnackbar(dataResponse.message || 'Failed to delete', 'error');
          }
          this.saving = false;
          this.deleteDialog = false;
          this.itemToDelete = null;
        },
        (errorResource) => {
          this.saving = false;
          this.showSnackbar('Error deleting', 'error');
          console.error(errorResource);
        }
      );
    },

    closeDialog() {
      this.dialog = false;
      this.formData = {
        targetPhraseID: null,
        phrase: '',
        activeFlag: true,
      };
      if (this.$refs.form) {
        this.$refs.form.reset();
      }
    },
  },
};
</script>

<style scoped>
.card2 {
  width: 100%;
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.btn-layout {
  padding: 14px 24px;
  width: 151px;
  height: 50px;
  background: linear-gradient(90deg, #504897 0%, #4b2667 100%) !important;
  border-radius: 16px;
  color: #fff !important;
  text-transform: none;
  font-weight: 500;
}

.btn-layout:hover {
  background: linear-gradient(90deg, #5a52a7 0%, #5b3077 100%) !important;
}

.dialog-header {
  background: linear-gradient(135deg, #f5f7fa 0%, #e8eef5 100%);
  border-bottom: 1px solid #e0e0e0;
}

.dialog-header-add {
  background: linear-gradient(135deg, #f0e6f6 0%, #e8d5f0 100%);
}

.dialog-header-delete {
  background: linear-gradient(135deg, #ffcdd2 0%, #ffebee 100%);
}

.header-icon-container {
  margin-right: 16px;
}

.icon-background {
  background: linear-gradient(135deg, #ffffff 0%, #f5f5f5 100%);
}

.icon-background-delete {
  background: linear-gradient(135deg, #ffffff 0%, #ffebee 100%);
}

.dialog-title {
  font-size: 18px;
  font-weight: 600;
  color: #333;
}

.dialog-subtitle {
  font-size: 13px;
  color: #666;
  margin-top: 2px;
}

.my-data-table {
  border-radius: 8px;
}

.gap-3 {
  gap: 12px;
}

.modern-table :deep(.v-table__wrapper) {
  max-height: 600px;
  overflow-y: auto;
  scrollbar-width: thin;
  scrollbar-color: rgba(25, 118, 210, 0.3) rgba(0, 0, 0, 0.05);
}

.modern-table :deep(.v-table__wrapper thead th) {
  position: sticky;
  top: 0;
  z-index: 2;
  background: rgb(var(--v-theme-surface, 255, 255, 255));
}

.modern-table :deep(.v-table__wrapper::-webkit-scrollbar) {
  width: 6px;
}

.modern-table :deep(.v-table__wrapper::-webkit-scrollbar-track) {
  background: rgba(0, 0, 0, 0.05);
}

.modern-table :deep(.v-table__wrapper::-webkit-scrollbar-thumb) {
  background: rgba(25, 118, 210, 0.3);
  border-radius: 3px;
}

.modern-table :deep(.v-table__wrapper::-webkit-scrollbar-thumb:hover) {
  background: rgba(25, 118, 210, 0.5);
}
</style>
